import { useQuery } from "@apollo/client";
import { useMemo, useState } from "react";
import type { PricingMode } from "@/gql/graphql";
import { SERVICES_QUERY } from "@/graphql/services";
import { WORKERS_QUERY } from "@/graphql/workers";
import { useSalonStore } from "@/lib/store/salon";

export interface SelectedService {
  id: string;
  name: string;
  duration: number;
  price: number;
  categoryName: string;
  pricingMode: PricingMode;
  selectedOptions?: string[];
  finalPrice?: number;
  workerId?: string; // Per-service worker assignment
}

interface UseServicesStepProps {
  onServicesChange?: (services: SelectedService[]) => void;
}

export function useServicesStep({ onServicesChange }: UseServicesStepProps = {}) {
  const { currentSalon, getSalonRole } = useSalonStore();

  // Services state
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Services query
  const {
    data: servicesData,
    loading: servicesLoading,
    error: servicesError,
  } = useQuery(SERVICES_QUERY, {
    variables: { salonId: currentSalon?.id || "" },
    skip: !currentSalon?.id,
  });

  // Workers query
  const {
    data: workersData,
    loading: workersLoading,
    error: workersError,
  } = useQuery(WORKERS_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
      filters: { isActive: true },
    },
    skip: !currentSalon?.id,
  });

  const services = servicesData?.services || [];
  const workers = workersData?.workers?.workers || [];

  // Get available categories
  const categories = useMemo(() => {
    const cats = ["All"];
    const uniqueCategories = new Set(services.map((s: any) => s.category?.name).filter(Boolean));
    cats.push(...Array.from(uniqueCategories));
    return cats;
  }, [services]);

  // Filter services based on search and category
  const filteredServices = useMemo(() => {
    return services.filter((service: any) => {
      const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || service.category?.name === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [services, searchTerm, selectedCategory]);

  // Calculate totals
  const totalDuration = useMemo(() => {
    return selectedServices.reduce((total, service) => total + service.duration, 0);
  }, [selectedServices]);

  const totalPrice = useMemo(() => {
    return selectedServices.reduce((total, service) => total + service.price, 0);
  }, [selectedServices]);

  // Handle service selection toggle
  const handleServiceToggle = (service: any) => {
    const isSelected = selectedServices.some((s) => s.id === service.id);

    let newServices: SelectedService[];
    if (isSelected) {
      newServices = selectedServices.filter((s) => s.id !== service.id);
    } else {
      const serviceToAdd: SelectedService = {
        id: service.id,
        name: service.name,
        duration: service.duration,
        price: service.basePrice || 0,
        categoryName: service.category?.name || "Uncategorized",
        pricingMode: service.pricingMode,
        selectedOptions: [],
        finalPrice: service.basePrice || 0,
      };
      newServices = [...selectedServices, serviceToAdd];
    }

    setSelectedServices(newServices);
    onServicesChange?.(newServices);
  };

  // Remove a specific service
  const removeService = (serviceId: string) => {
    const newServices = selectedServices.filter((s) => s.id !== serviceId);
    setSelectedServices(newServices);
    onServicesChange?.(newServices);
  };

  // Clear all selected services
  const clearServices = () => {
    setSelectedServices([]);
    setSearchTerm("");
    setSelectedCategory("All");
    onServicesChange?.([]);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedCategory("All");
  };

  // Check if service is selected
  const isServiceSelected = (serviceId: string) => {
    return selectedServices.some((s) => s.id === serviceId);
  };

  // Check if current user is owner
  const isOwner = currentSalon ? getSalonRole(currentSalon.id) === "OWNER" : false;

  // Validation
  const isValid = () => {
    return selectedServices.length > 0;
  };

  // Get validation error message
  const getValidationError = () => {
    if (selectedServices.length === 0) {
      return "Please select at least one service";
    }

    return null;
  };

  // Get validation warning message for owners
  const getValidationWarning = () => {
    return null;
  };

  return {
    // State
    selectedServices,
    setSelectedServices,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,

    // Data
    servicesData,
    services,
    workersData,
    workers,
    categories,
    filteredServices,
    totalDuration,
    totalPrice,

    // Actions
    handleServiceToggle,
    removeService,
    clearServices,
    resetFilters,
    isServiceSelected,

    // Validation
    isValid,
    getValidationError,
    getValidationWarning,
    isOwner,

    // Status
    loading: servicesLoading || workersLoading,
    error: servicesError || workersError,
  };
}
